/**
 * Small statistics helpers used by report slide renderers that compute
 * relationships between metrics in-component (correlation matrix, scatter).
 */

/**
 * Pearson correlation coefficient for a set of (x, y) pairs.
 * Returns 0 when there are fewer than two pairs or zero variance in either
 * axis (undefined correlation) — callers treat that as "no signal".
 */
export const correlation = (pairs: [number, number][]): number => {
  const n = pairs.length;
  if (n < 2) return 0;

  let sumX = 0;
  let sumY = 0;
  for (const [x, y] of pairs) {
    sumX += x;
    sumY += y;
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let cov = 0;
  let varX = 0;
  let varY = 0;
  for (const [x, y] of pairs) {
    const dx = x - meanX;
    const dy = y - meanY;
    cov += dx * dy;
    varX += dx * dx;
    varY += dy * dy;
  }

  const denom = Math.sqrt(varX * varY);
  if (denom === 0) return 0;
  const r = cov / denom;
  // Clamp against floating point drift beyond [-1, 1].
  return Math.max(-1, Math.min(1, r));
};
